/**
 * BetaAdminPage - Page d'administration pour la bêta ThaiPractice
 * URL obscure: /admin-beta-7x9k2m
 * Protégée par code admin (codes dynamiques en base)
 * 
 * Fonctionnalités:
 * - Génération de codes bêta
 * - Gestion des codes (révocation, réactivation)
 * - Libération du verrouillage appareil
 * - Stats par testeur
 * - Export CSV des logs
 * - Export JSON backup complet
 */
import React, { useState, useEffect } from 'react';
import { Smartphone, Unlock, Download, RefreshCw, Users, Activity, Shield, ChevronDown, ChevronUp } from 'lucide-react';

const BetaAdminPage = () => {
  const [adminCode, setAdminCode] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [codes, setCodes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [testerStats, setTesterStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('codes');
  const [generateCount, setGenerateCount] = useState(10);
  const [filterUser, setFilterUser] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [expandedCode, setExpandedCode] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check localStorage for admin code
  useEffect(() => {
    const stored = localStorage.getItem('adminCode');
    if (stored) {
      setAdminCode(stored);
      setAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    // Validate admin code via backend API instead of hardcoded list
    try {
      const response = await fetch(`${API_URL}/api/beta/admin/codes`, {
        headers: { 'X-Admin-Code': adminCode.toUpperCase() }
      });
      if (response.ok) {
        localStorage.setItem('adminCode', adminCode.toUpperCase());
        setAuthenticated(true);
        setError('');
      } else {
        setError('Code admin invalide');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/beta/admin/codes`, {
        headers: { 'X-Admin-Code': adminCode }
      });
      const data = await response.json();
      if (data.codes) {
        setCodes(data.codes);
      }
    } catch (err) {
      setError('Erreur lors du chargement des codes');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/beta/admin/logs?limit=200`;
      if (filterUser) url += `&user_id=${encodeURIComponent(filterUser)}`;
      if (filterModule) url += `&module=${encodeURIComponent(filterModule)}`;
      
      const response = await fetch(url, {
        headers: { 'X-Admin-Code': adminCode }
      });
      const data = await response.json();
      if (data.logs) {
        setLogs(data.logs);
      }
    } catch (err) {
      setError('Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/beta/admin/stats`, {
        headers: { 'X-Admin-Code': adminCode }
      });
      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError('Erreur lors du chargement des stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchTesterStats = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/beta/admin/stats-by-tester`, {
        headers: { 'X-Admin-Code': adminCode }
      });
      const data = await response.json();
      if (data.testers) {
        setTesterStats(data.testers);
      }
    } catch (err) {
      setError('Erreur lors du chargement des stats testeurs');
    } finally {
      setLoading(false);
    }
  };

  const generateCodes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/beta/admin/generate-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Code': adminCode
        },
        body: JSON.stringify({ count: generateCount, prefix: 'BETA' })
      });
      const data = await response.json();
      if (data.generated) {
        alert(`${data.count} codes générés !`);
        fetchCodes();
      }
    } catch (err) {
      setError('Erreur lors de la génération des codes');
    } finally {
      setLoading(false);
    }
  };

  const revokeCode = async (code) => {
    if (!window.confirm(`Révoquer le code ${code} ?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/beta/admin/revoke-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Code': adminCode
        },
        body: JSON.stringify({ code })
      });
      const data = await response.json();
      if (data.success) {
        fetchCodes();
      }
    } catch (err) {
      setError('Erreur lors de la révocation');
    }
  };

  const unrevokeCode = async (code) => {
    try {
      const response = await fetch(`${API_URL}/api/beta/admin/unrevoke-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Code': adminCode
        },
        body: JSON.stringify({ code })
      });
      const data = await response.json();
      if (data.success) {
        fetchCodes();
      }
    } catch (err) {
      setError('Erreur lors de la réactivation');
    }
  };

  const releaseDevice = async (code) => {
    if (!window.confirm(`Libérer l'appareil pour le code ${code} ?\n\nLe prochain appareil qui utilisera ce code sera enrôlé.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/beta/admin/release-device`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Code': adminCode
        },
        body: JSON.stringify({ code })
      });
      const data = await response.json();
      if (data.success) {
        alert('Appareil libéré avec succès');
        fetchCodes();
      }
    } catch (err) {
      setError('Erreur lors de la libération');
    }
  };

  const exportLogs = () => {
    let url = `${API_URL}/api/beta/admin/logs/export?admin_code_param=${encodeURIComponent(adminCode)}`;
    if (filterUser) url += `&user_id=${encodeURIComponent(filterUser)}`;
    if (filterModule) url += `&module=${encodeURIComponent(filterModule)}`;
    
    // Open download in new tab
    window.open(url, '_blank');
  };

  const exportBackup = () => {
    const url = `${API_URL}/api/beta/admin/backup?admin_code_param=${encodeURIComponent(adminCode)}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    if (authenticated) {
      fetchCodes();
      fetchStats();
    }
  }, [authenticated]);

  useEffect(() => {
    if (authenticated && activeTab === 'logs') {
      fetchLogs();
    }
    if (authenticated && activeTab === 'testers') {
      fetchTesterStats();
    }
  }, [authenticated, activeTab, filterUser, filterModule]);

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-xl p-6 max-w-sm w-full">
          <h1 className="text-xl font-bold text-white mb-4 text-center">Admin Bêta</h1>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={adminCode}
              onChange={(e) => setAdminCode(e.target.value.toUpperCase())}
              placeholder="Code admin"
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white mb-3 text-lg"
              autoFocus
              data-testid="admin-code-input"
            />
            {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
            <button
              type="submit"
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-lg font-medium min-h-[48px]"
              data-testid="admin-login-btn"
            >
              Connexion
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white p-3 sm:p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold">Admin Bêta ThaiPractice</h1>
          <div className="flex gap-2">
            <button
              onClick={exportBackup}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm min-h-[44px]"
              data-testid="backup-btn"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Backup JSON</span>
              <span className="sm:hidden">Backup</span>
            </button>
            <button
              onClick={() => {
                localStorage.removeItem('adminCode');
                setAuthenticated(false);
              }}
              className="text-slate-400 hover:text-white text-sm px-3 py-2 min-h-[44px]"
            >
              Déco
            </button>
          </div>
        </div>

        {/* Stats summary */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-slate-800 rounded-lg p-3 sm:p-4">
              <div className="text-xl sm:text-2xl font-bold text-emerald-400">{stats.active_codes}</div>
              <div className="text-slate-400 text-xs sm:text-sm">Codes actifs</div>
            </div>
            <div className="bg-slate-800 rounded-lg p-3 sm:p-4">
              <div className="text-xl sm:text-2xl font-bold text-blue-400">{stats.total_api_calls}</div>
              <div className="text-slate-400 text-xs sm:text-sm">Appels API</div>
            </div>
            <div className="bg-slate-800 rounded-lg p-3 sm:p-4">
              <div className="text-xl sm:text-2xl font-bold text-green-400">{stats.success_rate}%</div>
              <div className="text-slate-400 text-xs sm:text-sm">Taux succès</div>
            </div>
            <div className="bg-slate-800 rounded-lg p-3 sm:p-4">
              <div className="text-xl sm:text-2xl font-bold text-red-400">{stats.revoked_codes}</div>
              <div className="text-slate-400 text-xs sm:text-sm">Codes révoqués</div>
            </div>
          </div>
        )}

        {/* Tabs - Scrollable on mobile */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0">
          <button
            onClick={() => setActiveTab('codes')}
            className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg whitespace-nowrap min-h-[44px] ${activeTab === 'codes' ? 'bg-emerald-600' : 'bg-slate-700 hover:bg-slate-600'}`}
          >
            <Shield className="w-4 h-4" />
            <span>Codes ({codes.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('testers')}
            className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg whitespace-nowrap min-h-[44px] ${activeTab === 'testers' ? 'bg-emerald-600' : 'bg-slate-700 hover:bg-slate-600'}`}
          >
            <Users className="w-4 h-4" />
            <span>Testeurs</span>
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg whitespace-nowrap min-h-[44px] ${activeTab === 'logs' ? 'bg-emerald-600' : 'bg-slate-700 hover:bg-slate-600'}`}
          >
            <Activity className="w-4 h-4" />
            <span>Logs</span>
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-3 sm:px-4 py-2 rounded-lg whitespace-nowrap min-h-[44px] ${activeTab === 'stats' ? 'bg-emerald-600' : 'bg-slate-700 hover:bg-slate-600'}`}
          >
            Stats détaillées
          </button>
        </div>

        {error && (
          <div className="bg-red-900/50 border border-red-700 rounded-lg p-3 mb-4 text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Codes Tab */}
        {activeTab === 'codes' && (
          <div className="bg-slate-800 rounded-xl p-3 sm:p-4">
            {/* Generate codes */}
            <div className="flex flex-wrap gap-2 mb-4 items-center">
              <input
                type="number"
                value={generateCount}
                onChange={(e) => setGenerateCount(parseInt(e.target.value) || 1)}
                min="1"
                max="50"
                className="w-16 sm:w-20 px-2 sm:px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white min-h-[44px]"
              />
              <button
                onClick={generateCodes}
                disabled={loading}
                className="px-3 sm:px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-600 rounded-lg min-h-[44px] text-sm sm:text-base"
              >
                Générer
              </button>
              <button
                onClick={fetchCodes}
                disabled={loading}
                className="p-2 bg-slate-600 hover:bg-slate-500 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Codes list - Card layout on mobile */}
            <div className="space-y-2 sm:space-y-0">
              {/* SECTION ADMIN */}
              {codes.filter(c => c.is_admin || c.label?.includes('ADMIN')).length > 0 && (
                <>
                  <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-5 h-5 text-amber-400" />
                      <h3 className="text-amber-400 font-semibold">Codes Administrateur</h3>
                    </div>
                    <div className="space-y-2">
                      {codes.filter(c => c.is_admin || c.label?.includes('ADMIN')).map((c) => (
                        <div 
                          key={c.code}
                          className="bg-slate-800/50 rounded-lg p-3 flex justify-between items-center"
                        >
                          <div>
                            <span className="font-mono text-amber-400 font-bold">{c.code}</span>
                            <span className="text-slate-300 text-sm ml-3">{c.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {c.enrolled_device_id && (
                              <span className="text-xs text-blue-400 flex items-center gap-1">
                                <Smartphone className="w-3 h-3" /> Lié
                              </span>
                            )}
                            {c.is_revoked ? (
                              <span className="px-2 py-1 bg-red-900/50 text-red-400 text-xs rounded">Révoqué</span>
                            ) : (
                              <span className="px-2 py-1 bg-green-900/50 text-green-400 text-xs rounded">Actif</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* SECTION TESTEURS */}
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-emerald-400 font-semibold">Codes Testeurs</h3>
                  <span className="text-slate-400 text-sm">({codes.filter(c => !c.is_admin && !c.label?.includes('ADMIN')).length})</span>
                </div>
              </div>

              {/* Desktop table header */}
              <div className="hidden sm:grid sm:grid-cols-6 gap-2 text-left text-slate-400 text-sm border-b border-slate-700 pb-2 mb-2">
                <div>Code</div>
                <div>Label</div>
                <div>Créé le</div>
                <div>Appareil</div>
                <div>Statut</div>
                <div>Actions</div>
              </div>
              
              {codes.filter(c => !c.is_admin && !c.label?.includes('ADMIN')).map((c) => (
                <div 
                  key={c.code} 
                  className="bg-slate-700/50 sm:bg-transparent rounded-lg sm:rounded-none p-3 sm:p-0 sm:grid sm:grid-cols-6 sm:gap-2 sm:items-center sm:py-2 sm:border-b sm:border-slate-700/50"
                >
                  {/* Mobile: Expandable card */}
                  <div className="sm:hidden">
                    <div 
                      className="flex justify-between items-center cursor-pointer"
                      onClick={() => setExpandedCode(expandedCode === c.code ? null : c.code)}
                    >
                      <div>
                        <span className="font-mono text-emerald-400 text-sm">{c.code}</span>
                        <span className="text-slate-400 text-xs ml-2">{c.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {c.enrolled_device_id && (
                          <Smartphone className="w-4 h-4 text-blue-400" />
                        )}
                        {c.revoked ? (
                          <span className="text-red-400 text-xs">Révoqué</span>
                        ) : (
                          <span className="text-green-400 text-xs">Actif</span>
                        )}
                        {expandedCode === c.code ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </div>
                    
                    {/* Expanded mobile content */}
                    {expandedCode === c.code && (
                      <div className="mt-3 pt-3 border-t border-slate-600 space-y-2">
                        <div className="text-xs text-slate-400">
                          Créé: {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '-'}
                        </div>
                        {c.enrolled_device_id && (
                          <div className="text-xs text-slate-400">
                            Appareil: {c.enrolled_device_id.slice(0, 8)}... ({c.enrolled_ip || 'IP inconnue'})
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {c.enrolled_device_id && (
                            <button
                              onClick={() => releaseDevice(c.code)}
                              className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 hover:bg-amber-700 rounded-lg text-sm min-h-[44px]"
                              data-testid={`release-device-btn-${c.code}`}
                            >
                              <Unlock className="w-4 h-4" />
                              Libérer
                            </button>
                          )}
                          {c.revoked ? (
                            <button
                              onClick={() => unrevokeCode(c.code)}
                              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm min-h-[44px]"
                            >
                              Réactiver
                            </button>
                          ) : (
                            <button
                              onClick={() => revokeCode(c.code)}
                              className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm min-h-[44px]"
                            >
                              Révoquer
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Desktop: Table row */}
                  <div className="hidden sm:block font-mono text-emerald-400 text-sm">{c.code}</div>
                  <div className="hidden sm:block text-sm">{c.label}</div>
                  <div className="hidden sm:block text-slate-400 text-xs">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '-'}
                  </div>
                  <div className="hidden sm:block">
                    {c.enrolled_device_id ? (
                      <div className="flex items-center gap-1 text-xs text-blue-400" title={`IP: ${c.enrolled_ip || 'N/A'}`}>
                        <Smartphone className="w-3 h-3" />
                        <span>{c.enrolled_device_id.slice(0, 8)}...</span>
                      </div>
                    ) : (
                      <span className="text-slate-500 text-xs">Non enrôlé</span>
                    )}
                  </div>
                  <div className="hidden sm:block">
                    {c.revoked ? (
                      <span className="text-red-400 text-sm">Révoqué</span>
                    ) : (
                      <span className="text-green-400 text-sm">Actif</span>
                    )}
                  </div>
                  <div className="hidden sm:flex gap-2">
                    {c.enrolled_device_id && (
                      <button
                        onClick={() => releaseDevice(c.code)}
                        className="flex items-center gap-1 text-amber-400 hover:text-amber-300 text-xs min-h-[32px] px-2"
                        title="Libérer l'appareil"
                        data-testid={`release-device-btn-${c.code}`}
                      >
                        <Unlock className="w-3 h-3" />
                        Libérer
                      </button>
                    )}
                    {c.revoked ? (
                      <button
                        onClick={() => unrevokeCode(c.code)}
                        className="text-blue-400 hover:text-blue-300 text-xs"
                      >
                        Réactiver
                      </button>
                    ) : (
                      <button
                        onClick={() => revokeCode(c.code)}
                        className="text-red-400 hover:text-red-300 text-xs"
                      >
                        Révoquer
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {codes.length === 0 && (
                <p className="text-center text-slate-500 py-8">Aucun code généré</p>
              )}
            </div>
          </div>
        )}

        {/* Testers Tab - Stats per tester */}
        {activeTab === 'testers' && (
          <div className="bg-slate-800 rounded-xl p-3 sm:p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold">Activité par testeur</h3>
              <button
                onClick={fetchTesterStats}
                disabled={loading}
                className="p-2 bg-slate-600 hover:bg-slate-500 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            
            <div className="space-y-3">
              {testerStats.map((tester) => (
                <div key={tester.code} className="bg-slate-700/50 rounded-lg p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
                    <div>
                      <div className="font-mono text-emerald-400 text-sm">{tester.code}</div>
                      <div className="text-slate-300">{tester.label}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {tester.enrolled ? (
                        <span className="flex items-center gap-1 text-xs text-blue-400">
                          <Smartphone className="w-3 h-3" />
                          Enrôlé
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">Non enrôlé</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
                    <div className="bg-slate-600/50 rounded p-2">
                      <div className="text-lg font-bold text-blue-400">{tester.api_calls}</div>
                      <div className="text-xs text-slate-400">Appels API</div>
                    </div>
                    <div className="bg-slate-600/50 rounded p-2">
                      <div className="text-lg font-bold text-emerald-400">{tester.practice_sessions}</div>
                      <div className="text-xs text-slate-400">Sessions</div>
                    </div>
                    <div className="bg-slate-600/50 rounded p-2">
                      <div className="text-lg font-bold text-purple-400">{tester.srs_cards_reviewed}</div>
                      <div className="text-xs text-slate-400">Cartes SRS</div>
                    </div>
                    <div className="bg-slate-600/50 rounded p-2">
                      <div className="text-lg font-bold text-amber-400">{tester.custom_words}</div>
                      <div className="text-xs text-slate-400">Mots perso</div>
                    </div>
                    <div className="bg-slate-600/50 rounded p-2 col-span-2 sm:col-span-1">
                      <div className="text-xs text-slate-400">Dernière activité</div>
                      <div className="text-xs text-slate-300">
                        {tester.last_activity 
                          ? new Date(tester.last_activity).toLocaleString('fr-FR', { 
                              day: '2-digit', 
                              month: '2-digit', 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })
                          : 'Jamais'
                        }
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {testerStats.length === 0 && (
                <p className="text-center text-slate-500 py-8">Aucune donnée testeur</p>
              )}
            </div>
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div className="bg-slate-800 rounded-xl p-3 sm:p-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
              <input
                type="text"
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                placeholder="Filtrer par utilisateur..."
                className="flex-1 min-w-[150px] px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm min-h-[44px]"
              />
              <select
                value={filterModule}
                onChange={(e) => setFilterModule(e.target.value)}
                className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm min-h-[44px]"
              >
                <option value="">Tous modules</option>
                <option value="ploy">Manee</option>
                <option value="story">Histoires</option>
                <option value="tts">TTS Audio</option>
                <option value="whisper">Whisper STT</option>
                <option value="quiz">Quiz</option>
                <option value="translate">Traduction</option>
                <option value="explain">Explications</option>
              </select>
              <button
                onClick={fetchLogs}
                disabled={loading}
                className="p-2 bg-slate-600 hover:bg-slate-500 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={exportLogs}
                className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm min-h-[44px]"
              >
                <Download className="w-4 h-4" />
                CSV
              </button>
            </div>

            {/* Logs table */}
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-800">
                  <tr className="text-left text-slate-400 border-b border-slate-700">
                    <th className="p-2">Date/Heure</th>
                    <th className="p-2">Utilisateur</th>
                    <th className="p-2">Module</th>
                    <th className="p-2 hidden sm:table-cell">Endpoint</th>
                    <th className="p-2 hidden sm:table-cell">Tokens</th>
                    <th className="p-2">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={i} className="border-b border-slate-700/50">
                      <td className="p-2 text-slate-400 text-xs">
                        {log.timestamp ? new Date(log.timestamp).toLocaleString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </td>
                      <td className="p-2 text-xs">{log.user_id}</td>
                      <td className="p-2">
                        <span className="px-2 py-0.5 bg-slate-700 rounded text-xs">{log.module}</span>
                      </td>
                      <td className="p-2 text-slate-400 text-xs font-mono hidden sm:table-cell">{log.endpoint}</td>
                      <td className="p-2 text-xs hidden sm:table-cell">
                        {log.tokens_input || log.tokens_output ? (
                          <span>{log.tokens_input || 0}→{log.tokens_output || 0}</span>
                        ) : log.audio_duration_ms ? (
                          <span>{Math.round(log.audio_duration_ms/1000)}s</span>
                        ) : '-'}
                      </td>
                      <td className="p-2">
                        {log.success ? (
                          <span className="text-green-400">✓</span>
                        ) : (
                          <span className="text-red-400" title={log.error_message}>✗</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {logs.length === 0 && (
                <p className="text-center text-slate-500 py-8">Aucun log</p>
              )}
            </div>
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="bg-slate-800 rounded-xl p-3 sm:p-4">
            <h3 className="font-bold mb-4">Par module</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
              {Object.entries(stats.by_module || {}).map(([module, count]) => (
                <div key={module} className="bg-slate-700 rounded-lg p-3">
                  <div className="text-lg font-bold">{count}</div>
                  <div className="text-slate-400 text-sm">{module}</div>
                </div>
              ))}
            </div>

            <h3 className="font-bold mb-4">Par utilisateur</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(stats.by_user || {}).map(([user, count]) => (
                <div key={user} className="bg-slate-700 rounded-lg p-3">
                  <div className="text-lg font-bold">{count}</div>
                  <div className="text-slate-400 text-sm truncate">{user}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BetaAdminPage;
